/**
 * UpgradeRedirectPage — one-click coupon-applied checkout.
 *
 * Landing page reached from Day 7 upsell email:
 *   `/upgrade?c=SNAPSHOT50&p=pro_monthly&e=user%40example.com`
 *
 * Validates the coupon, shows the discounted price for confidence,
 * and immediately fires `/api/payments/checkout` with the coupon
 * applied — then redirects to Stripe. Manual fallback button is
 * shown if the auto-redirect fails (slow network, ad-blocker).
 *
 * Owner-facing goal: eliminate the "type the code at checkout"
 * friction point, typically +15-25 % conversion on the email-to-pay
 * step versus a copy-paste flow.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { API } from "../config";
import { trackEvent } from "../lib/analytics";

const DEFAULTS = { c: "SNAPSHOT50", p: "pro_monthly" };

export default function UpgradeRedirectPage() {
  const [params] = useSearchParams();
  const couponCode = params.get("c") || DEFAULTS.c;
  const packageId = params.get("p") || DEFAULTS.p;
  const initialEmail = params.get("e") || "";

  const [email, setEmail] = useState(initialEmail);
  const [validation, setValidation] = useState(null); // null | {valid:bool, ...}
  const [stage, setStage] = useState("validating"); // 'validating' | 'ready' | 'redirecting' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  // Validate the coupon on mount so we can display the discount value
  // before the user clicks anything.
  useEffect(() => {
    let cancelled = false;
    axios
      .post(`${API}/payments/validate-coupon`, {
        code: couponCode,
        package_id: packageId,
        email: initialEmail || null,
      })
      .then((r) => {
        if (cancelled) return;
        setValidation(r.data);
        setStage(r.data?.valid ? "ready" : "error");
        if (!r.data?.valid) setErrorMsg(r.data?.error || "Coupon not valid");
      })
      .catch(() => {
        if (!cancelled) {
          setStage("error");
          setErrorMsg("Couldn't reach the coupon service. Please try again.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [couponCode, packageId, initialEmail]);

  // Auto-fire checkout once we have an email + valid coupon, so most
  // users never see a button — they go straight to Stripe.
  useEffect(() => {
    if (stage === "ready" && email && email.includes("@")) {
      const t = setTimeout(() => startCheckout(), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [stage, email]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async () => {
    if (!email.includes("@")) return;
    setStage("redirecting");
    trackEvent("start_checkout", {
      value: validation?.discounted_amount_eur,
      currency: "EUR",
      item_id: packageId,
      coupon: couponCode,
      source: "upgrade_redirect_email",
    });
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_email: email,
        language: "en",
        coupon: couponCode,
        success_url: `${window.location.origin}/dashboard?payment=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/upgrade?c=${couponCode}&p=${packageId}`,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        setStage("error");
        setErrorMsg("Couldn't start checkout. Please try again.");
      }
    } catch {
      setStage("error");
      setErrorMsg("Couldn't start checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#141414] border border-zinc-800 rounded-2xl p-8 shadow-2xl" data-testid="upgrade-redirect-card">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-7 h-7 text-[#FFE600]" />
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Pro · Coupon applied</span>
        </div>

        {stage === "validating" && (
          <div className="text-center py-6" data-testid="upgrade-validating">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">Applying your discount…</p>
          </div>
        )}

        {stage !== "validating" && validation?.valid && (
          <>
            <h1 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-3" data-testid="upgrade-title">
              Your −{validation.percent_off}% discount is ready
            </h1>
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-bold">Code {validation.code} applied</p>
                <p className="text-xs text-zinc-400 mt-1">
                  First month:{" "}
                  <span className="line-through text-zinc-600">€{validation.base_amount_eur}</span>{" "}
                  <span className="text-emerald-300 font-bold" data-testid="upgrade-discounted-price">
                    €{validation.discounted_amount_eur}
                  </span>
                  {" · "}then €{validation.base_amount_eur}/mo. Cancel anytime.
                </p>
              </div>
            </div>

            <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-2">
              Email for checkout
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="upgrade-email-input"
              className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FFE600] mb-4"
            />

            <button
              type="button"
              disabled={!email.includes("@") || stage === "redirecting"}
              onClick={startCheckout}
              data-testid="upgrade-continue-btn"
              className="w-full bg-[#FFE600] text-black font-bold py-3 rounded-lg hover:bg-[#FFF066] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {stage === "redirecting" ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</span>
              ) : (
                <>Pay €{validation.discounted_amount_eur} & start Pro →</>
              )}
            </button>

            <p className="text-[11px] text-zinc-600 mt-3 text-center">
              🔒 Secure payment via Stripe · 14-day money-back guarantee
            </p>
          </>
        )}

        {stage === "error" && (
          <div data-testid="upgrade-error">
            <div className="flex items-start gap-3 mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-zinc-300">{errorMsg}</p>
            </div>
            <Link
              to="/pricing"
              className="block w-full text-center bg-zinc-800 hover:bg-zinc-700 font-bold py-3 rounded-lg text-sm transition-colors"
              data-testid="upgrade-fallback-pricing"
            >
              See all pricing options
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
